import { useField, useFormikContext } from 'formik'
import React, { useMemo } from 'react'
import {
  Props as ReactSelectProps,
  SingleValue,
  StylesConfig,
} from 'react-select'

import FieldError from '@/components/form/FieldError'
import FieldLabel from '@/components/form/FieldLabel'
import SingleSelectOptionLabel, {
  SingleSelectOptionLabelProps,
} from '@/components/form/SingleSelectOptionLabel'
import Select from '@/components/select/Select'
import { THEME_PALETTE_DARK } from '@/lib/constants'
import { getCssVariable } from '@/lib/helpers/domHelpers'
import { SelectOption } from '@/types/misc'

export interface SingleSelectProps {
  autoFocus?: boolean
  label?: React.ReactNode
  name: string
  noOptionsMessage?: ReactSelectProps['noOptionsMessage']
  onSelect?: (option: SelectOption | null) => void
  onInputChange?: ReactSelectProps['onInputChange']
  options: SelectOption[]
  disabledOptions?: SelectOption[]
  placeholder?: string
  isDisabled?: boolean
  isClearable?: boolean
  formatOptionLabel?: (renderProps: {
    renderDefaultOptionLabel: () => React.ReactNode
    option: SelectOption
  }) => React.ReactNode
  renderDisabledOption?: SingleSelectOptionLabelProps['renderDisabledOption']
  className?: string
  selectedOption?: SelectOption | null
}

const SingleSelectInput: React.FC<SingleSelectProps> = ({
  autoFocus,
  label,
  name,
  noOptionsMessage,
  onSelect,
  onInputChange,
  options,
  disabledOptions,
  placeholder,
  isDisabled,
  isClearable = true,
  formatOptionLabel,
  renderDisabledOption,
  className,
  selectedOption,
}) => {
  const { submitCount } = useFormikContext()
  const [field, { error }, { setValue: setFieldValue }] = useField<
    string | null
  >(name)
  const id = `field_${name}`
  const showError = !!(submitCount > 0 && error)

  function handleSelect(option: SingleValue<SelectOption>) {
    setFieldValue(option ? option.value : '')

    if (onSelect) {
      onSelect(option)
    }
  }

  const styles: StylesConfig<SelectOption, false> = useMemo(
    () => ({
      menu: (provided) => ({
        ...provided,
        marginTop: 0,
        zIndex: 5, // To ensure menu appears above form controls
      }),
      menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
      control: (provided, { isDisabled }) => ({
        ...provided,
        color: THEME_PALETTE_DARK.secondary,
        backgroundColor: isDisabled ? getCssVariable('--disabled') : undefined,
        opacity: isDisabled ? '50%' : undefined,
      }),
      menuList: (provided) => ({
        ...provided,
        paddingTop: 5,
      }),
      singleValue: (provided) => ({
        ...provided,
        color: 'inherit',
      }),
      option: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
        color: 'inherit',
        ':active': {
          backgroundColor: 'transparent',
        },
      }),
    }),
    [],
  )

  const selectedValue =
    typeof selectedOption !== 'undefined'
      ? selectedOption
      : options.find((option) => option.value === field.value) || null

  return (
    <>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}

      <Select
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        inputId={id}
        styles={styles}
        isClearable={isClearable}
        options={options}
        onInputChange={onInputChange}
        value={selectedValue}
        formatOptionLabel={(option, meta) => {
          function renderDefaultOptionLabel() {
            return (
              <SingleSelectOptionLabel
                option={option}
                meta={meta}
                fieldName={name}
                isSelected={option.value === field.value}
                isDisabled={(disabledOptions || [])
                  .map((option) => option.value)
                  .includes(option.value)}
                renderDisabledOption={renderDisabledOption}
              />
            )
          }

          return formatOptionLabel
            ? formatOptionLabel({
                renderDefaultOptionLabel,
                option,
              })
            : renderDefaultOptionLabel()
        }}
        noOptionsMessage={noOptionsMessage}
        onChange={handleSelect}
        placeholder={placeholder}
        className={className}
      />

      {showError && <FieldError inputId={id}>{error}</FieldError>}
    </>
  )
}

export default SingleSelectInput
